import React, { useState, useEffect } from 'react';
import { X, User, Phone, Mail, MapPin } from 'lucide-react';
import { Customer, updateCustomer, getCustomer } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';
import AddressAutocomplete from './common/AddressAutocomplete';

interface ModifyCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (customer: Customer) => void;
  onError?: (error: string | null) => void;
  customer: Customer | null;
  bookingId?: string;
}

export default function ModifyCustomerModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
  customer,
  bookingId
}: ModifyCustomerModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    type: 'person' | 'business';
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    address: string;
    tax_exempt: boolean;
    workflow_exempt: boolean;
    receive_reminders: boolean;
    receive_offers: boolean;
    notes: string;
  }>({
    type: 'person',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    tax_exempt: false,
    workflow_exempt: false,
    receive_reminders: true,
    receive_offers: true,
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setError(null);
      if (onError) onError(null);

      // Validate customer object first
      if (!customer) {
        const error = 'Unable to modify customer: No customer provided';
        console.error('Customer validation failed:', {
          customer,
          bookingId,
          businessId: selectedBusiness?.id
        });
        setError(error);
        if (onError) onError(error);
        return;
      }

      // Load fresh customer data
      const loadCustomer = async () => {
        try {
          // Clear any existing errors
          setError(null);
          if (onError) onError(null);

          // Validate customer ID
          if (!customer.id || typeof customer.id !== 'string') {
            const error = `Unable to modify customer: Invalid customer ID [${customer?.id}]`;
            console.error('Customer validation failed:', {
              customerId: customer?.id,
              customerType: typeof customer?.id,
              customer: customer,
              bookingId,
              businessId: selectedBusiness?.id
            });
            setError(error);
            if (onError) onError(error);
            return;
          }

          // Log attempt to load customer
          console.log('Loading customer details...', {
            customerId: customer.id, 
            bookingId,
            customerEmail: customer.email,
            businessId: selectedBusiness?.id
          }); 

          const freshCustomer = await getCustomer(customer.id);
          
          if (!freshCustomer) {
            const error = `Unable to modify customer: Customer ${customer.id} not found`;
            console.error('Customer not found:', {
              customerId: customer.id,
              bookingId,
              businessId: selectedBusiness?.id
            });
            setError(error);
            if (onError) onError(error);
            return;
          }

          // Log successful load
          console.log('Customer loaded successfully', {
            customerId: freshCustomer.id,
            name: `${freshCustomer.first_name} ${freshCustomer.last_name}`,
            businessId: selectedBusiness?.id
          });

          setFormData({
            type: freshCustomer.type ?? 'person',
            first_name: freshCustomer.first_name ?? '',
            last_name: freshCustomer.last_name ?? '',
            email: freshCustomer.email ?? '',
            phone: freshCustomer.phone ?? '',
            address: freshCustomer.address ?? '',
            tax_exempt: freshCustomer.tax_exempt || false,
            workflow_exempt: freshCustomer.workflow_exempt || false,
            receive_reminders: freshCustomer.receive_reminders ?? true,
            receive_offers: freshCustomer.receive_offers ?? true,
            notes: freshCustomer.notes ?? ''
          });
          setError(null);
          if (onError) onError(null);
        } catch (err) {
          console.error('Error loading customer:', {
            error: err,
            customerId: customer.id,
            bookingId,
            businessId: selectedBusiness?.id
          });
          const errorMessage = err instanceof Error ? err.message : 'Failed to load customer details';
          setError(errorMessage);
          if (onError) {
            onError(errorMessage);
          }
        }
      };
      loadCustomer().catch(err => console.error('Unexpected error loading customer:', err));
    }
  }, [isOpen, customer]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate customer object first
    if (!customer) {
      const errorMessage = 'Unable to save changes: No customer provided';
      console.error('Invalid customer:', {
        customer,
        bookingId,
        businessId: selectedBusiness?.id
      });
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
      return;
    }
    
    // Validate customer ID
    if (!customer.id) {
      const errorMessage = `Unable to save changes: Invalid customer ID [${customer?.id}]`;
      console.error('Invalid customer ID:', {
        customer,
        customerId: customer?.id,
        bookingId,
        businessId: selectedBusiness?.id
      });
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
      return;
    }
    
    const validationErrors: string[] = [];
    try {
      setLoading(true);
      setError(null);

      console.log('Updating customer:', {
        id: customer.id,
        formData,
        bookingId,
        businessId: selectedBusiness?.id
      });

      // Validate all required fields first
      if (!formData.first_name?.trim()) {
        validationErrors.push('First name is required');
      }
      if (!formData.last_name?.trim()) {
        validationErrors.push('Last name is required');
      }
      if (!formData.phone?.trim()) {
        validationErrors.push('Phone number is required');
      }

      // If there are validation errors, throw them all at once
      if (validationErrors.length > 0) {
        const errorMessage = validationErrors.join(', ');
        console.error('Validation errors:', {
          customerId: customer.id,
          errors: validationErrors,
          bookingId,
          businessId: selectedBusiness?.id
        });
        throw new Error(errorMessage);
      }

      // Convert empty strings to null for optional fields
      const customerData = {
        ...formData,
        email: formData.email.trim() || null,
        address: formData.address.trim() || null,
        notes: formData.notes.trim() || null
      };

      const updatedCustomer = await updateCustomer(customer.id, customerData);
      console.log('Customer updated successfully:', {
        customer: updatedCustomer,
        bookingId,
        businessId: selectedBusiness?.id
      });
      if (onError) onError(null);
      
      onSuccess(updatedCustomer);
    } catch (err) {
      console.error('Error updating customer:', {
        error: err,
        customerId: customer.id,
        bookingId,
        businessId: selectedBusiness?.id
      });
      const errorMessage = err instanceof Error ? err.message : 'Failed to update customer details';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      console.log('Customer update operation completed');
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!customer) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[999999]`}
      onClick={onClose}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-[#2f2f2f] bg-[#1a1a1a] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Modify Customer</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
            <div className="space-y-8">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Error Modifying Customer
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        {error}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Enter first name"
                    value={formData.first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Enter last name"
                    value={formData.last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-6">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      required
                      placeholder="Enter phone number"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      placeholder="Enter email address (optional)"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full h-10 rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    <AddressAutocomplete
                      value={formData.address}
                      onChange={(address) => setFormData(prev => ({ ...prev, address }))}
                      placeholder="Enter address (optional)"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={4}
                  className="w-full rounded-md bg-gray-50 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm px-4 py-2"
                  placeholder="Add any additional notes about this customer..."
                />
              </div>

              {/* Preferences */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-6">Preferences</h3>
                <div className="space-y-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.tax_exempt}
                      onChange={(e) => setFormData(prev => ({ ...prev, tax_exempt: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                    />
                    <span className="ml-2 text-sm text-gray-700">Tax exempt</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.workflow_exempt}
                      onChange={(e) => setFormData(prev => ({ ...prev, workflow_exempt: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                    />
                    <span className="ml-2 text-sm text-gray-700">Workflow exempt</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.receive_reminders}
                      onChange={(e) => setFormData(prev => ({ ...prev, receive_reminders: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                    />
                    <span className="ml-2 text-sm text-gray-700">Receive automated reminders</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.receive_offers}
                      onChange={(e) => setFormData(prev => ({ ...prev, receive_offers: e.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                    />
                    <span className="ml-2 text-sm text-gray-700">Receive exclusive offers and news</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}